/* istanbul ignore file */
import { pdfjs } from 'react-pdf';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import ApplicationRouter from './ApplicationRouter';
import InfrastructureProviders from './InfrastructureProviders';
import { PageTitle } from 'framework/PageTitle';
import SingletonHooksProvider from './SingletonHooksProvider';
import { useArtifactInfoService, isLinkExpired } from 'domain/shared/artifact';
import { ArtifactRestoreDialogProvider } from 'domain/shared/artifact/ArtifactRestoreDialog';
import {
  useArtifactsInfoReceiver,
  useInvoiceArtifactsInfoReceiver,
  useCandidateArtifactsInfoReceiver,
  useProjectArtifactsInfoReceiver,
  useDebouncedBatchReceiver,
} from 'domain/shared/artifact/receivers';
import GlobalPermissionsProvider from 'infrastructure/security/authorization/GlobalPermissionsProvider';
import AuthorizationAuthProviderWrapper from 'infrastructure/security/authorization/AuthorizationAuthProviderWrapper';
import AuthenticationProviderWrapper from 'infrastructure/security/authentication/AuthenticationProviderWrapper';
import DefaultTokenProviderWrapper from 'infrastructure/security/authentication/DefaultTokenProviderWrapper';
import { CustomThemeProvider } from 'framework/ui/themes';
import { CssBaseline } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import DavinciSettings from 'infrastructure/DavinciSettings';
import HtmlFieldLayoutWithTags from 'domain/shared/artifact/field/HtmlFieldLayoutWithTags';
import { FeatureToggleProvider } from 'framework/featureToggle';
import { featureToggles } from 'infrastructure/featureToggle';
import { store, persistor } from 'app/store/store';
import { Scroll } from 'framework/utils';
import { useEmployeeNamesInfoReceiver } from 'domain/shared/employee';
import useMeService from 'domain/people/employee/meService';
import Audit from 'infrastructure/security/audit/Audit';
import UrlTokenProvider from 'infrastructure/security/UrlTokenProvider';
import GraphQLProvider from 'infrastructure/graphql/GraphQLProvider';
import { AuthenticatedUserDependenciesProvider } from 'infrastructure/security/authentication/authenticatedUserDependencies';
import { ToastProvider } from 'framework/ui/Toast';
import { MessageToasts } from 'framework/ui/Message';
import InterruptionsEmergency from 'domain/people/availability/interruprions/InterruptionsEmergency/components/InterruptionsEmergencyOverlay';
import AvailabilityProvider from 'domain/people/availability/AvailabilityProvider';
import HighlightUrlWrapper from './HighlightUrlWrapper';

import './App.scss';
import 'font-awesome/css/font-awesome.min.css';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Served from our own bundle: the worker sees every PDF opened in the portal.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const App = () => (
  <StyledEngineProvider injectFirst>
    <CustomThemeProvider>
      <CssBaseline />
      <ToastProvider>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <FeatureToggleProvider featureToggles={featureToggles}>
              <DefaultTokenProviderWrapper>
                <AuthenticatedUserDependenciesProvider
                  value={{
                    useMeService,
                    useArtifactInfoService,
                    isLinkExpired,
                  }}
                >
                  <AuthenticationProviderWrapper>
                    <AuthorizationAuthProviderWrapper>
                      <AvailabilityProvider>
                        <Scroll />
                        <MessageToasts />
                        <Audit>
                          <UrlTokenProvider>
                            <SingletonHooksProvider
                              hooksForEagerInitialization={[
                                useDebouncedBatchReceiver,
                                useArtifactsInfoReceiver,
                                useProjectArtifactsInfoReceiver,
                                useCandidateArtifactsInfoReceiver,
                                useEmployeeNamesInfoReceiver,
                                useInvoiceArtifactsInfoReceiver,
                              ]}
                            />
                          </UrlTokenProvider>
                          <InfrastructureProviders>
                            <DavinciSettings htmlFieldLayout={HtmlFieldLayoutWithTags}>
                              <PageTitle
                                allowedSubParts={[
                                  'crm',
                                  'one-stop',
                                  'signin',
                                  'audit',
                                  'processes',
                                  'payroll',
                                  'my-activity',
                                  'me',
                                ]}
                                specificSubParts={['qualification-models']}
                              >
                                <GlobalPermissionsProvider>
                                  <GraphQLProvider>
                                    <ArtifactRestoreDialogProvider>
                                      <HighlightUrlWrapper>
                                        <ApplicationRouter />
                                      </HighlightUrlWrapper>
                                      <InterruptionsEmergency />
                                    </ArtifactRestoreDialogProvider>
                                  </GraphQLProvider>
                                </GlobalPermissionsProvider>
                              </PageTitle>
                            </DavinciSettings>
                          </InfrastructureProviders>
                        </Audit>
                      </AvailabilityProvider>
                    </AuthorizationAuthProviderWrapper>
                  </AuthenticationProviderWrapper>
                </AuthenticatedUserDependenciesProvider>
              </DefaultTokenProviderWrapper>
            </FeatureToggleProvider>
          </PersistGate>
        </Provider>
      </ToastProvider>
    </CustomThemeProvider>
  </StyledEngineProvider>
);

export default App;
